import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { EmptyState, ErrorState } from '@/components/shared/states';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/shared/form-field';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasAllPermissions, hasPermission, PERMISSIONS } from '@/lib/permissions';
import { PeopleSubnav } from '@/pages/people/people-subnav';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatDateTime } from '@/utils/format';

import { MemberCreateDialog } from './member-create-dialog';
import { formToPayload, joinRequestToMemberForm, type MemberFormState } from './member-form-state';

export interface SuggestedMemberMatch {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  memberType: string;
  status: string;
  linked: boolean;
  matchReasons: Array<'email' | 'phone' | 'name'>;
}

export interface MemberJoinRequestRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  gender: string | null;
  dateOfBirth: string | null;
  status: string;
  createdAt: string;
  userEmail: string;
  userPhone: string | null;
  userFirstName: string;
  userLastName: string;
  suggestedMatches: SuggestedMemberMatch[];
}

const displayName = (row: Pick<MemberJoinRequestRow, 'firstName' | 'lastName'>) =>
  [row.firstName, row.lastName].filter(Boolean).join(' ');

const churchLocalDate = (timezone: string) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone || 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

export const JoinRequestReviewDialog = ({
  request,
  open,
  onOpenChange,
}: {
  request: MemberJoinRequestRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('people');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useChurchStore((state) => state.membership);
  const church = useChurchStore((state) => state.church);
  const timezone = usePreferences((state) => state.timezone);
  const canViewContact = hasPermission(membership, PERMISSIONS.PEOPLE_VIEW_CONTACT);
  const canMerge = hasAllPermissions(membership, [
    PERMISSIONS.PEOPLE_CREATE,
    PERMISSIONS.PEOPLE_EDIT,
  ]);
  const [note, setNote] = useState('');
  const [mergeTarget, setMergeTarget] = useState<SuggestedMemberMatch | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<MemberJoinRequestRow | null>(request);

  useEffect(() => {
    setCurrentRequest(request);
    setNote('');
    setMergeTarget(null);
    setCreateOpen(false);
  }, [request]);

  const refreshMatches = useMutation({
    mutationFn: () =>
      api.post<MemberJoinRequestRow>(`/people/join-requests/${currentRequest!.id}/duplicate-check`),
    onSuccess: (updated) => {
      setCurrentRequest(updated);
      toast.success(t('joinRequests.duplicates.refreshed'));
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : t('joinRequests.review.error'));
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['people', 'join-requests'] });
    void queryClient.invalidateQueries({ queryKey: ['people', 'join-requests', 'pending-count'] });
  };

  const approve = useMutation({
    mutationFn: (form: MemberFormState) =>
      api.post<MemberJoinRequestRow>(`/people/join-requests/${currentRequest!.id}/approve`, {
        decisionNote: note.trim() || null,
        member: formToPayload(form),
      }),
    onSuccess: () => {
      toast.success(t('joinRequests.review.approved'));
      invalidate();
      setCreateOpen(false);
      onOpenChange(false);
      setNote('');
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : t('joinRequests.review.error'));
    },
  });

  const merge = useMutation({
    mutationFn: () =>
      api.post<MemberJoinRequestRow>(`/people/join-requests/${currentRequest!.id}/merge`, {
        memberId: mergeTarget!.id,
        decisionNote: note.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('joinRequests.review.merged'));
      invalidate();
      onOpenChange(false);
      setMergeTarget(null);
      setNote('');
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : t('joinRequests.review.error'));
    },
  });

  const reject = useMutation({
    mutationFn: () =>
      api.post<MemberJoinRequestRow>(`/people/join-requests/${currentRequest!.id}/reject`, {
        decisionNote: note.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('joinRequests.review.rejected'));
      invalidate();
      onOpenChange(false);
      setNote('');
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : t('joinRequests.review.error'));
    },
  });

  if (!currentRequest) return null;

  const busy = approve.isPending || merge.isPending || reject.isPending || refreshMatches.isPending;

  return (
    <>
      <Dialog open={open && !createOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('joinRequests.review.title', { name: displayName(currentRequest) })}</DialogTitle>
          </DialogHeader>
          <DialogBody className="overflow-visible" contentClassName="space-y-6">
            <div className="flex flex-wrap justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={refreshMatches.isPending}
                onClick={() => refreshMatches.mutate()}
              >
                {t('joinRequests.duplicates.checkOne')}
              </Button>
            </div>
            <section className="space-y-2">
              <h3 className="text-body-sm font-medium text-fg-primary">{t('joinRequests.review.submitted')}</h3>
              <dl className="grid gap-2 text-body-sm sm:grid-cols-2">
                <div>
                  <dt className="text-fg-secondary">{t('joinRequests.review.name')}</dt>
                  <dd>{displayName(currentRequest)}</dd>
                </div>
                <div>
                  <dt className="text-fg-secondary">{t('joinRequests.review.email')}</dt>
                  <dd>{canViewContact ? (currentRequest.email ?? '—') : t('members.contactHidden')}</dd>
                </div>
                <div>
                  <dt className="text-fg-secondary">{t('joinRequests.review.phone')}</dt>
                  <dd>{canViewContact ? (currentRequest.phone ?? '—') : t('members.contactHidden')}</dd>
                </div>
                <div>
                  <dt className="text-fg-secondary">{t('joinRequests.review.submittedAt')}</dt>
                  <dd>{formatDateTime(currentRequest.createdAt)}</dd>
                </div>
              </dl>
            </section>

            <section className="space-y-2">
              <h3 className="text-body-sm font-medium text-fg-primary">{t('joinRequests.review.account')}</h3>
              <dl className="grid gap-2 text-body-sm sm:grid-cols-2">
                <div>
                  <dt className="text-fg-secondary">{t('joinRequests.review.accountName')}</dt>
                  <dd>{[currentRequest.userFirstName, currentRequest.userLastName].filter(Boolean).join(' ')}</dd>
                </div>
                <div>
                  <dt className="text-fg-secondary">{t('joinRequests.review.accountEmail')}</dt>
                  <dd>{currentRequest.userEmail}</dd>
                </div>
                <div>
                  <dt className="text-fg-secondary">{t('joinRequests.review.accountPhone')}</dt>
                  <dd>{currentRequest.userPhone ?? '—'}</dd>
                </div>
              </dl>
            </section>

            {currentRequest.suggestedMatches.length > 0 ? (
              <section className="space-y-3">
                <div>
                  <h3 className="text-body-sm font-medium text-fg-primary">
                    {t('joinRequests.review.suggestedTitle')}
                  </h3>
                  <p className="text-body-sm text-fg-secondary">{t('joinRequests.review.suggestedHint')}</p>
                </div>
                <ul className="space-y-2">
                  {currentRequest.suggestedMatches.map((match) => (
                    <li
                      key={match.id}
                      className="flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <MemberNameLink
                            memberId={match.id}
                            name={[match.firstName, match.lastName].filter(Boolean).join(' ')}
                            className="font-medium"
                          />
                          {match.linked ? (
                            <Badge variant="neutral">{t('joinRequests.review.alreadyLinked')}</Badge>
                          ) : null}
                        </div>
                        <p className="text-body-sm text-fg-secondary">
                          {canViewContact
                            ? [match.email, match.phone].filter(Boolean).join(' · ') || '—'
                            : t('members.contactHidden')}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {match.matchReasons.map((reason) => (
                            <Badge key={reason} variant="neutral">
                              {t(`joinRequests.review.matchReason.${reason}`)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {canMerge ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={match.linked || busy}
                          onClick={() => setMergeTarget(match)}
                        >
                          {t('joinRequests.review.connect')}
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : (
              <p className="text-body-sm text-fg-secondary">{t('joinRequests.review.noSuggestions')}</p>
            )}

            <FormField label={t('joinRequests.review.note')}>
              {(field) => (
                <Textarea
                  id={field.id}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  placeholder={t('joinRequests.review.notePlaceholder')}
                />
              )}
            </FormField>

            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
                {t('joinRequests.review.close')}
              </Button>
              <Button variant="destructive" disabled={busy} onClick={() => reject.mutate()}>
                {t('joinRequests.review.decline')}
              </Button>
              <Button disabled={busy} onClick={() => setCreateOpen(true)}>
                {t('joinRequests.review.addNew')}
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <MemberCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        resetKey={currentRequest.id}
        initialValues={joinRequestToMemberForm(currentRequest, {
          country: church?.country,
          firstContactOn: churchLocalDate(timezone),
        })}
        title={t('joinRequests.review.addFormTitle', { name: displayName(currentRequest) })}
        description={t('joinRequests.review.addFormDescription')}
        submitLabel={t('members.form.save')}
        submitting={approve.isPending}
        onSubmit={async (form) => {
          await approve.mutateAsync(form);
        }}
      />

      <ConfirmDialog
        open={mergeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setMergeTarget(null);
        }}
        title={t('joinRequests.review.mergeConfirmTitle')}
        description={t('joinRequests.review.mergeConfirmDescription', {
          requestName: displayName(currentRequest),
          memberName: mergeTarget ? [mergeTarget.firstName, mergeTarget.lastName].join(' ') : '',
        })}
        confirmLabel={t('joinRequests.review.connect')}
        cancelLabel={t('joinRequests.review.close')}
        onConfirm={() => merge.mutate()}
        loading={merge.isPending}
      />
    </>
  );
};

export const JoinRequestsPage = () => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const membership = useChurchStore((state) => state.membership);
  const canViewContact = hasPermission(membership, PERMISSIONS.PEOPLE_VIEW_CONTACT);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<MemberJoinRequestRow | null>(null);

  const list = useQuery({
    queryKey: ['people', 'join-requests', page],
    queryFn: () =>
      api.get<{
        data: MemberJoinRequestRow[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(
        `/people/join-requests?page=${page}&pageSize=25&status=pending`,
      ),
  });

  const columns: DataTableColumn<MemberJoinRequestRow>[] = [
    {
      id: 'submittedAt',
      header: t('joinRequests.columns.submittedAt'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'name',
      header: t('joinRequests.columns.name'),
      cell: (row) => displayName(row),
    },
    {
      id: 'contact',
      header: t('joinRequests.columns.contact'),
      cell: (row) =>
        canViewContact
          ? [row.email, row.phone].filter(Boolean).join(' · ') || '—'
          : t('members.contactHidden'),
    },
    {
      id: 'matches',
      header: t('joinRequests.columns.matches'),
      cell: (row) =>
        row.suggestedMatches.length > 0
          ? t('joinRequests.columns.matchCount', { count: row.suggestedMatches.length })
          : '—',
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  return (
    <div>
      <PageHeader
        title={t('joinRequests.title')}
        description={t('joinRequests.description')}
        headerActions={
          <Button variant="outline" onClick={() => navigate('/people/join-requests/duplicate-check')}>
            <Copy className="mr-2 size-4" />
            {t('joinRequests.duplicates.action')}
          </Button>
        }
      />
      <PeopleSubnav />
      {list.isError ? (
        <ErrorState
          title={t('joinRequests.error.title')}
          body={t('joinRequests.error.description')}
          onRetry={() => void list.refetch()}
        />
      ) : (
        <>
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            loading={list.isLoading}
            empty={
              <EmptyState
                title={t('joinRequests.empty')}
                body={t('joinRequests.emptyDescription')}
              />
            }
            onRowClick={(row) => setSelected(row)}
          />
          {pageMeta && pageMeta.totalPages > 1 ? (
            <div className="mt-4">
              <Pagination
                page={pageMeta.page}
                pageSize={pageMeta.pageSize}
                totalItems={pageMeta.totalItems}
                totalPages={pageMeta.totalPages}
                onPageChange={setPage}
                labels={{
                  showing: ({ from, to, total }) => `${from}–${to} / ${total}`,
                  perPage: tCommon('pagination.perPage', { defaultValue: 'Per page' }),
                  first: tCommon('pagination.first', { defaultValue: 'First' }),
                  previous: tCommon('pagination.previous', { defaultValue: 'Previous' }),
                  next: tCommon('pagination.next', { defaultValue: 'Next' }),
                  last: tCommon('pagination.last', { defaultValue: 'Last' }),
                }}
              />
            </div>
          ) : null}
        </>
      )}
      <JoinRequestReviewDialog
        request={selected}
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />
    </div>
  );
};
